import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import CircularProgress from '@mui/material/CircularProgress';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import type { TimelineState } from '@xzdarcy/react-timeline-editor';

import VideoEditingService, { VideoClip, EditorTimeline } from 'services/VideoEditingService';
import TimelinePreview, { TimelinePreviewHandle } from './components/TimelinePreview';
import { timelineDuration } from './timelineModel';
import { formatDuration } from './format';

// PUBLIC, read-only preview of a saved edit — reached by the shareable no-login
// link embedded in the generated editor instructions. It plays the whole edit
// so an external editor can see roughly what it should look like. There is no
// editing here by design: no trim, add, drag, or autosave — just playback.
const service = new VideoEditingService();

// The public endpoint returns only { fileId, streamUrl } per clip. Fill it out
// to the VideoClip shape TimelinePreview reads; dimensions are unknown, so the
// player falls back to a 9/16 frame (the vertical UGC these edits are made from).
function toVideoClip(c: { fileId: string; streamUrl: string }): VideoClip {
  return {
    fileId: c.fileId,
    name: '',
    mimeType: '',
    sizeBytes: null,
    durationSec: null,
    width: null,
    height: null,
    createdTime: null,
    streamUrl: c.streamUrl,
  };
}

type LoadState = 'loading' | 'ready' | 'expired' | 'notfound' | 'error';

export default function VideoEditingPreview() {
  const { planId } = useParams<{ planId: string }>();
  const query = new URLSearchParams(useLocation().search);
  const exp = query.get('exp') || '';
  const sig = query.get('sig') || '';

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [name, setName] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<EditorTimeline | null>(null);
  const [clipsById, setClipsById] = useState<Map<string, VideoClip>>(new Map());

  const previewRef = useRef<TimelinePreviewHandle>(null);
  const timelineStateRef = useRef<TimelineState | null>(null);
  const [playing, setPlaying] = useState(false);
  const [displayTime, setDisplayTime] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    service.getPublicPreview(planId, exp, sig)
      .then(({ plan, clips }) => {
        if (cancelled) return;
        setName(plan.name);
        setTimeline(plan.timeline);
        setClipsById(new Map(clips.map((c) => [c.fileId, toVideoClip(c)])));
        setLoadState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setLoadState(status === 410 ? 'expired' : status === 404 ? 'notfound' : 'error');
      });
    return () => { cancelled = true; };
  }, [planId, exp, sig]);

  const total = useMemo(() => (timeline ? timelineDuration(timeline) : 0), [timeline]);

  const togglePlay = () => {
    const p = previewRef.current;
    if (!p) return;
    if (p.isPlaying()) { p.pause(); return; }
    if (displayTime >= total - 0.05) p.seek(0);
    p.play();
  };

  // The player reports every frame; the scrubber and the m:ss readout can't show
  // more than a tenth of a second, and re-rendering the whole stage 60×/s is what
  // makes the scrubber lurch on a loaded machine. Mirrors the editor's throttle.
  const lastReported = useRef(0);
  const onTime = (t: number) => {
    if (Math.abs(t - lastReported.current) < 0.1) return;
    lastReported.current = t;
    setDisplayTime(t);
  };

  const seekTo = (t: number) => {
    lastReported.current = t;
    setDisplayTime(t);
    previewRef.current?.seek(t);
  };

  const message = (title: string, detail: string) => (
    <Box sx={{ maxWidth: 420, textAlign: 'center', px: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{detail}</Typography>
    </Box>
  );

  return (
    <Box sx={{
      minHeight: '100vh', width: '100%', backgroundColor: '#0f172a', color: '#fff',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, py: 4,
    }}>
      {loadState === 'loading' && <CircularProgress sx={{ color: '#fff' }} />}

      {loadState === 'expired' && message('This preview link has expired', 'Ask for a fresh link — reopen the project in the editor and generate the instructions again.')}
      {loadState === 'notfound' && message('Preview not found', 'This project may have been deleted, or the link is incomplete.')}
      {loadState === 'error' && message('Could not load this preview', 'Something went wrong loading the edit. Try the link again in a moment.')}

      {loadState === 'ready' && timeline && (
        <>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{name || 'Untitled edit'}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Read-only preview — this plays the whole edit so you can see roughly what it should look like.
            </Typography>
          </Box>

          <Box sx={{ width: '100%', maxWidth: 480, px: 2 }}>
            <TimelinePreview
              ref={previewRef}
              timeline={timeline}
              clipsById={clipsById}
              timelineStateRef={timelineStateRef}
              onPlayStateChange={setPlaying}
              onTimeChange={onTime}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
              <IconButton onClick={togglePlay} sx={{ color: '#fff' }} aria-label={playing ? 'Pause' : 'Play'}>
                {playing ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <Slider
                aria-label="Seek"
                size="small"
                min={0}
                max={Math.max(total, 0.1)}
                step={0.1}
                value={Math.min(displayTime, total)}
                onChange={(_e, v) => seekTo(v as number)}
                sx={{ color: '#818cf8' }}
              />
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 92, color: 'rgba(255,255,255,0.8)' }}>
                {formatDuration(displayTime)} / {formatDuration(total)}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
